import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../redux/middleware/users';
import './StaticHeader.css';
import logo from '../../assets/images/header_logo_green.png';

const OWNER_EMAIL = 'thedoglodgesd@gmail.com';

const StaticHeader = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.users.user);
  const isEmployeeUser = user?.isEmployee || false;
  const isAdminUser = user?.isAdmin || false;
  const isOwner = user?.email === OWNER_EMAIL;
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Close menu on route change
  useEffect(() => {
    return history.listen(() => {
      setIsMenuOpen(false);
    });
  }, [history]);

  const menuItems = [
    { path: '/secure', label: 'Dashboard', icon: '🏠' },
    { path: '/secure/updates', label: 'Updates', icon: '📥' },
    { path: '/secure/today', label: "Today's Bookings", icon: '📅' },
    { path: '/secure/clients', label: 'Clients', icon: '👥' },
    { path: '/secure/animals', label: 'Pets', icon: '🐾' },
    { path: '/secure/bookings', label: 'Bookings', icon: '📆' },
    { path: '/secure/booking-requests', label: 'Booking Requests', icon: '📩' },
    { path: '/secure/vaccinations', label: 'Vaccinations', icon: '💉' },
    { path: '/secure/leads', label: 'Leads', icon: '📋' },
    { path: '/secure/outreach', label: 'Outreach', icon: '📧' },
    { path: '/secure/check-ins', label: 'Check Ins', icon: '✅' },
    { path: '/secure/employees', label: 'Employees', icon: '👨‍💼' },
    { path: '/secure/post-queue', label: 'Instagram', icon: '📸' },
    { path: '/secure/change-log', label: 'Change Log', icon: '📝' },
    ...(isOwner ? [{ path: '/secure/revenue', label: 'Revenue', icon: '💰' }] : []),
    ...(isOwner ? [{ path: '/secure/projections', label: 'Projections', icon: '📈' }] : []),
    ...(isOwner ? [{ path: '/secure/admin/blog', label: 'Blog Queue', icon: '✏️' }] : []),
  ];

  return (
    <header className="static-header">
      <div className="static-header-container">
        <div className="static-header-left">
          <span className="static-header-title">Admin Portal</span>
        </div>
        
        <div className="static-header-center">
          <div className="static-header-logo" onClick={() => {
            if (location.pathname === '/secure') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              history.push('/secure');
            }
          }}>
            <img src={logo} alt="The Dog Lodge" />
          </div>
        </div>

        <div className="static-header-right">
          <button 
            ref={buttonRef}
            className={`static-header-menu-button ${isMenuOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <span className="menu-icon-bar"></span>
            <span className="menu-icon-bar"></span>
            <span className="menu-icon-bar"></span>
          </button>

          <div 
            ref={menuRef}
            className={`static-header-dropdown ${isMenuOpen ? 'open' : ''}`}
          >
            <nav className="static-header-nav">
              {menuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  exact={item.path === '/secure'}
                  activeClassName="active"
                  className="static-header-nav-item"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              ))}
            </nav>

            <div className="static-header-dropdown-footer">
              {/* Public Website button - navigate to public home page */}
              <button 
                className="nav-button home"
                onClick={() => {
                  setIsMenuOpen(false);
                  history.push('/');
                }}
              >
                <span className="nav-icon">🏪</span>
                <span className="nav-label">Public Website</span>
              </button>

              {/* Employee Dashboard button (shown for both admin and employee) */}
              {(isAdminUser || isEmployeeUser) && (
                <button 
                  className="nav-button employee-home"
                  onClick={() => {
                    setIsMenuOpen(false);
                    history.push('/employee');
                  }}
                >
                  <span className="nav-icon">👷</span>
                  <span className="nav-label">Employee Dashboard</span>
                </button>
              )}

              {/* Admin Home button (shown for admin only) */}
              {isAdminUser && (
                <button 
                  className="nav-button admin-home"
                  onClick={() => {
                    setIsMenuOpen(false);
                    history.push('/secure');
                  }}
                >
                  <span className="nav-icon">🏠</span>
                  <span className="nav-label">Admin Home</span>
                </button>
              )}

              {/* Logout button */}
              <button 
                className="nav-button logout"
                onClick={async () => {
                  await dispatch(logout());
                  history.push('/');
                }}
              >
                <span className="nav-icon">🚪</span>
                <span className="nav-label">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default StaticHeader;